/**
 * Webinars — contract types for the Marketing ▸ Webinars surface.
 *
 * A webinar is an EVENT that walks through five ordered steps, each of which
 * reuses a system we already run rather than growing a parallel one:
 *
 *   1. `invite`    — the invitation email. Creates a real crm-api email
 *                    campaign with the existing `webinar` layout, so the
 *                    operator edits + sends it in the campaign editor they
 *                    already know. The webinar only remembers its id.
 *   2. `attendees` — who actually turned up. The operator pastes / uploads
 *                    the attendee list the webinar platform exported; the
 *                    system stores it and creates the THANK-YOU campaign
 *                    targeted at exactly those people.
 *   3. `content`   — the recording becomes marketing. Claude (via the AI
 *                    gateway `/agent/complete`) writes a blog post + a
 *                    LinkedIn post from the transcript/notes; gpt-image-1
 *                    (via `/image`) renders XOP-branded hero art. Everything
 *                    is exportable (Markdown / HTML / PNG) for the real blog.
 *   4. `survey`    — a PUBLIC feedback form hosted on the customer support
 *                    site (support.xopnetworks.com), because respondents have no
 *                    dashboard login. The dashboard publishes the survey to
 *                    portal-api and reads its stats + individual answers back.
 *   5. `leads`     — every attendee is scored as a sales lead by the AI, and
 *                    the good ones are pushed into the CRM as prospects for
 *                    the sales team to chase.
 *
 * **Nothing in this feature ever sends an email by itself.** Steps 1, 2 and 4
 * each CREATE a campaign in `not_sent` (draft) state and hand the operator a
 * link; a human presses Send in the campaign editor. See docs/webinars.md.
 */

/** The five ordered steps. The array order IS the wizard order. */
export const WEBINAR_STEPS = [
  "invite",
  "attendees",
  "content",
  "survey",
  "leads",
] as const;

export type WebinarStep = (typeof WEBINAR_STEPS)[number];

/** 1-based position of a step in the wizard (0 when the step is unknown). */
export function webinarStepNumber(step: WebinarStep): number {
  return WEBINAR_STEPS.indexOf(step) + 1;
}

/** Human labels — one source of truth for the stepper, the list page's
 * progress column, and the e2e specs. Deliberately plain-English: the
 * operator should never have to decode a noun like "post-event nurture". */
export const WEBINAR_STEP_LABELS: Record<WebinarStep, string> = {
  invite: "Invite people",
  attendees: "Who joined",
  content: "Blog + LinkedIn",
  survey: "Ask how it went",
  leads: "Find leads",
};

/** One-line "what do I do here" blurb rendered under each step title. */
export const WEBINAR_STEP_HINTS: Record<WebinarStep, string> = {
  invite: "Make the invitation email and send it to the people you want there.",
  attendees: "Paste the list of people who actually joined, then thank them.",
  content: "Turn the recording into a blog post and a LinkedIn post.",
  survey: "Ask the people who joined how it went, and read their answers.",
  leads: "Find leads among the people who joined, and send the good ones to sales.",
};

/** Lifecycle of the whole event. `draft` until step 1 is done, `running`
 * while the operator works through the steps, `complete` once all five are
 * done. Purely derived on the server from step completion — never set by
 * hand — so the list page and the detail page can never disagree. */
export type WebinarStatus = "draft" | "running" | "complete";

export interface Webinar {
  id: number;
  title: string;
  /** Short "what is this webinar about" summary. Feeds the invite copy and
   * the AI content prompts. */
  description: string;
  /** ISO-8601 UTC instant the webinar starts. */
  scheduled_at: string | null;
  duration_minutes: number;
  /** Where attendees join (Zoom/Teams/conferweb link). Rendered into the
   * invitation email and the calendar attachment. */
  registration_link: string;
  /** Link to the recording, for the operator's own reference. The AI reads
   * `transcript`, not this — we never fetch a video. */
  recording_url: string;
  /** The transcript / speaker notes the AI writes the blog + LinkedIn post
   * from. Empty until the operator pastes it in step 3. */
  transcript: string;
  status: WebinarStatus;
  /** crm-api email-campaign ids. Null until the step creates them. */
  invite_campaign_id: number | null;
  thankyou_campaign_id: number | null;
  survey_campaign_id: number | null;
  /** Opaque token that addresses the PUBLIC survey page on the support
   * site: `<support>/webinar-survey/<survey_token>`. Null until published. */
  survey_token: string | null;
  /** Steps the operator has completed, in no particular order. */
  completed_steps: WebinarStep[];
  /** The first step that is NOT complete — what the wizard opens on. Null
   * once every step is done. */
  next_step: WebinarStep | null;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateWebinarRequest {
  title: string;
  description?: string;
  scheduled_at?: string | null;
  duration_minutes?: number;
  registration_link?: string;
}

export interface UpdateWebinarRequest {
  title?: string;
  description?: string;
  scheduled_at?: string | null;
  duration_minutes?: number;
  registration_link?: string;
  recording_url?: string;
  transcript?: string;
}

// ── Attendees ─────────────────────────────────────────────────────────

/** Where the sales-lead verdict for an attendee currently stands. */
export type WebinarLeadState = "unscored" | "scored" | "pushed" | "skipped";

export interface WebinarAttendee {
  id: number;
  webinar_id: number;
  name: string;
  email: string;
  company: string;
  job_title: string;
  phone: string;
  /** Did they actually turn up? The import marks everyone in the platform's
   * attendee export as joined; a registrant-only row is `false`. Only joined
   * attendees are thanked, surveyed, and scored. */
  joined: boolean;
  /** Minutes present, when the export carries it. 0 = unknown. */
  minutes_attended: number;
  lead_state: WebinarLeadState;
  /** 0–100 fit score from the AI. Null until scored. */
  lead_score: number | null;
  /** Why the AI scored it that way, in one or two sentences. */
  lead_reason: string;
  /** What the AI thinks sales should do next. */
  lead_recommendation: string;
  /** crm-api Lead (Customer STI, type='Lead') id once pushed. */
  crm_lead_id: number | null;
  created_at: string;
}

/** The parsed result of one pasted/uploaded attendee list, before it is
 * written. Surfaced so the UI can show "12 people found, 1 line skipped"
 * BEFORE the operator commits — a five-year-old-proof preview. */
export interface WebinarAttendeeImportPreview {
  rows: ParsedAttendeeRow[];
  /** Lines we could not read at all (no email). Echoed back verbatim so the
   * operator can see exactly what was dropped. */
  skipped: string[];
}

export interface ParsedAttendeeRow {
  name: string;
  email: string;
  company: string;
  job_title: string;
  phone: string;
  joined: boolean;
  minutes_attended: number;
}

export interface ImportAttendeesRequest {
  /** Raw pasted text — CSV, TSV, or one-email-per-line. Parsed server-side
   * so the browser and the server can never disagree about the result. */
  text: string;
  /** When true, rows whose email already exists on this webinar are updated
   * instead of duplicated. Always true today; explicit for readability. */
  merge?: boolean;
}

export interface ImportAttendeesResult {
  added: number;
  updated: number;
  skipped: string[];
  attendees: WebinarAttendee[];
}

// ── Step 3: generated content ─────────────────────────────────────────

export type WebinarContentKind = "blog" | "linkedin";

export const WEBINAR_CONTENT_KINDS: readonly WebinarContentKind[] = [
  "blog",
  "linkedin",
];

export interface WebinarContentPiece {
  id: number;
  webinar_id: number;
  kind: WebinarContentKind;
  title: string;
  /** Markdown. The blog post is long-form; the LinkedIn post is the short
   * platform-native version (no Markdown headings, hashtags at the end). */
  body_markdown: string;
  /** The prompt we sent gpt-image-1, kept so a regenerate is reproducible
   * and so the operator can see what the picture was asked to show. */
  image_prompt: string;
  /** base64 PNG, or null when image generation was skipped / unavailable.
   * Stripped from LIST responses (they are ~1 MB each) — fetch the single
   * piece to get it. */
  image_b64: string | null;
  /** Whether a stored image exists, so a list response can badge it
   * without shipping the bytes. */
  has_image: boolean;
  /** Which model wrote the text / drew the image, for the operator's own
   * audit ("Claude wrote this"). */
  text_model: string;
  image_model: string;
  created_at: string;
}

export interface GenerateContentRequest {
  kind: WebinarContentKind;
  /** Optional operator steer ("lean on the 911 resilience angle"). */
  hint?: string;
  /** Generate branded hero art alongside the text. Default true. */
  with_image?: boolean;
}

// ── Step 4: the public survey ─────────────────────────────────────────

/** One question on the survey. Kept deliberately tiny — a rating scale and
 * free text answer every "how was it / what should we improve" need, and a
 * bigger question type system would be a form builder, not a webinar. */
export interface WebinarSurveyQuestion {
  key: string;
  prompt: string;
  type: "rating" | "text";
  required: boolean;
}

/** The default questionnaire. The operator can edit the prompts but not the
 * shape, so the stats page always knows how to aggregate. */
export const DEFAULT_WEBINAR_SURVEY_QUESTIONS: readonly WebinarSurveyQuestion[] = [
  {
    key: "overall",
    prompt: "Overall, how was the webinar?",
    type: "rating",
    required: true,
  },
  {
    key: "content",
    prompt: "How useful was the content?",
    type: "rating",
    required: true,
  },
  {
    key: "speaker",
    prompt: "How clear were the speakers?",
    type: "rating",
    required: true,
  },
  {
    key: "improve",
    prompt: "What should we improve next time?",
    type: "text",
    required: false,
  },
  {
    key: "topics",
    prompt: "What would you like us to cover in a future webinar?",
    type: "text",
    required: false,
  },
];

export interface WebinarSurveyResponse {
  id: number;
  /** Present when the respondent followed their personal link; anonymous
   * (null) when they used the shared public link. */
  respondent_email: string | null;
  /** question key → 1–5 rating. */
  ratings: Record<string, number>;
  /** question key → free-text answer. */
  answers: Record<string, string>;
  submitted_at: string;
}

export interface WebinarSurveyStats {
  published: boolean;
  /** Public URL of the survey on the support site. Null until published. */
  public_url: string | null;
  invited: number;
  responses: number;
  /** responses / invited, 0–100, rounded to one decimal. 0 when nobody
   * was invited (never NaN — a NaN here rendered as "NaN%" on the card). */
  response_rate: number;
  /** question key → mean rating (1 decimal), for rating questions only. */
  averages: Record<string, number>;
  /** question key → { 1..5 → count } histogram, for rating questions. */
  distribution: Record<string, Record<string, number>>;
  questions: WebinarSurveyQuestion[];
  recent: WebinarSurveyResponse[];
}

export interface PublishSurveyRequest {
  /** Override the default question prompts. Same keys + types; only the
   * wording changes. Omit to publish {@link DEFAULT_WEBINAR_SURVEY_QUESTIONS}. */
  questions?: WebinarSurveyQuestion[];
}

// ── Step 5: lead scoring ──────────────────────────────────────────────

export interface ScoreLeadsResult {
  scored: number;
  attendees: WebinarAttendee[];
}

export interface PushLeadsRequest {
  /** Attendee ids to push into the CRM as prospects. */
  attendee_ids: number[];
}

export interface PushLeadsResult {
  /** Newly created in the CRM. */
  pushed: number;
  /** The CRM already had this company, so the attendee was LINKED to the
   * existing record rather than creating a duplicate. Not a failure — the
   * outcome the operator wanted, reached a different way. */
  already_in_crm: number;
  failed: { attendee_id: number; reason: string }[];
  attendees: WebinarAttendee[];
}

/** The score at or above which we call an attendee a lead worth chasing.
 * Shared so the server's "recommended" default selection and the UI's green
 * badge can never drift apart. */
export const WEBINAR_GOOD_LEAD_SCORE = 60;

// ── Aggregate detail payload ──────────────────────────────────────────

/** Everything the detail page needs in ONE read, so the wizard can render
 * complete on first paint instead of popping in five times. */
export interface WebinarDetail {
  webinar: Webinar;
  attendees: WebinarAttendee[];
  content: WebinarContentPiece[];
  survey: WebinarSurveyStats;
  /** Live status of the three linked campaigns, read from crm-api. Absent
   * entries mean "not created yet"; a null value means crm-api could not be
   * reached (the UI shows "couldn't check", never a false "not sent"). */
  campaigns: Record<string, WebinarCampaignSummary | null>;
}

export interface WebinarCampaignSummary {
  id: number;
  subject: string;
  status: string;
  recipients_count: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
}
